/**
 * MetaConversions — página "Conversões" do módulo Meta Ads (Conversions API, issue #317).
 *
 * Configura o envio dos eventos de negócio do CRM à Meta: conta e dataset (pixel), interruptor,
 * mapa etapa do funil -> evento, evento de teste e o log de envios (com reenvio das falhas).
 * Só owner/admin. Só vale o que acontecer DEPOIS de ligar o envio; o histórico não é enviado.
 * Hash, deduplicação e permissão são do backend; aqui só mostramos o resultado.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { DataTable } from '../../components/DataTable.jsx'
import Layout from '../../components/Layout.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getMetaConversionsDatasets, getMetaConversionsLog, getMetaConversionsSettings, retryMetaConversion,
  saveMetaConversionsSettings, sendMetaConversionsTest,
} from '../../db/api.js'
import { useAccess } from '../../hooks/useAccess.js'
import {
  LOG_FILTERS, LOG_STATUS, SOURCE_LABELS, buildSavePayload, canRetry, formatValue, formatWhen, groupStages,
  hasChanges, identifiersText, initialMap, mappedCount, validateForm,
} from '../../lib/metaConversions.js'
import {
  demoDatasets, demoLoad, demoLog, demoRetry, demoSave, demoTest,
} from '../../lib/metaConversionsDemo.js'
import { isDemo } from '../../lib/metaCampaignDemo.js'
import { useLocation } from 'react-router-dom'
import PageState from '../../components/PageState.jsx'
import './metaPages.css'

// Sem período: é configuração do envio de eventos (Conversions API), não um relatório por data.
const BREADCRUMB = [{ label: 'Marketing' }, { label: 'Meta Ads', to: '/meta-ads' }, { label: 'Conversões' }]

function Card({ title, sub, children, aside }) {
  return (
    <section className="mcn-card">
      <div className="mcn-card-head">
        <div>
          <div className="mcn-card-title">{title}</div>
          {sub && <div className="mcn-card-sub">{sub}</div>}
        </div>
        {aside}
      </div>
      {!sub && <div className="mcn-card-spacer" />}
      {children}
    </section>
  )
}

function Field({ label, hint, error, children }) {
  return (
    <div className="field">
      {label && <label className="field-label">{label}</label>}
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="mcn-error" role="alert">{error}</div>}
    </div>
  )
}

export default function MetaConversions() {
  const { activeClient } = useApp()
  const { isOrgAdmin } = useAccess()
  const demo = isDemo(useLocation().search)
  const clientId = activeClient?.id ?? null

  const api = useMemo(() => (demo
    ? { load: demoLoad, datasets: demoDatasets, save: demoSave, test: demoTest, log: demoLog, retry: demoRetry }
    : {
      load: getMetaConversionsSettings, datasets: getMetaConversionsDatasets, save: saveMetaConversionsSettings,
      test: sendMetaConversionsTest, log: getMetaConversionsLog, retry: retryMetaConversion,
    }), [demo])

  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [form, setForm] = useState(null)
  const [saved, setSaved] = useState(null)
  const [pixels, setPixels] = useState({ list: [], warning: null })
  const [showErrors, setShowErrors] = useState(false)
  const [busy, setBusy] = useState(null)
  const [notice, setNotice] = useState(null)
  const [testCode, setTestCode] = useState('')
  const [filter, setFilter] = useState('')
  const [log, setLog] = useState([])
  const [logLoading, setLogLoading] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  // Carrega a configuração do cliente.
  useEffect(() => {
    if (!clientId || !isOrgAdmin) return undefined
    let alive = true
    setData(null)
    setLoadError(null)
    api.load(clientId)
      .then((res) => {
        if (!alive) return
        const next = {
          accountId: res.settings?.meta_account_id || res.accounts?.[0]?.id || '',
          datasetId: res.settings?.dataset_id || '',
          enabled: Boolean(res.settings?.enabled),
          map: initialMap(res.event_map, res.stages),
        }
        setData(res)
        setForm(next)
        setSaved(res.settings ? { ...next, map: initialMap(res.event_map, res.stages) } : null)
      })
      .catch((err) => { if (alive) setLoadError(err.message || 'Não foi possível carregar a configuração.') })
    return () => { alive = false }
  }, [clientId, isOrgAdmin, api, reloadKey])

  // Datasets (pixels) da conta escolhida.
  useEffect(() => {
    if (!clientId || !form?.accountId) return undefined
    let alive = true
    api.datasets({ clientId, accountId: form.accountId })
      .then((res) => { if (alive) setPixels({ list: res.pixels || [], warning: (res.warnings || [])[0] || null }) })
      .catch((err) => { if (alive) setPixels({ list: [], warning: err.message || 'Não foi possível listar os datasets.' }) })
    return () => { alive = false }
  }, [clientId, form?.accountId, api])

  const loadLog = useCallback(async () => {
    if (!clientId) return
    setLogLoading(true)
    try {
      setLog(await api.log({ clientId, result: filter, limit: 50 }))
    } catch {
      setLog([])
    } finally {
      setLogLoading(false)
    }
  }, [clientId, filter, api])

  useEffect(() => { if (data) loadLog() }, [data, loadLog])

  const errors = form ? validateForm(form) : {}
  const groups = useMemo(() => groupStages(data?.stages), [data])
  const dirty = form ? hasChanges(form, saved) : false
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const setEvent = (stageId, event) => setForm((f) => ({ ...f, map: { ...f.map, [stageId]: event } }))

  async function save() {
    if (Object.keys(errors).length) { setShowErrors(true); return }
    setBusy('save')
    setNotice(null)
    try {
      await api.save(buildSavePayload(form, clientId))
      setSaved({ ...form, map: { ...form.map } })
      setShowErrors(false)
      setNotice({ tone: 'ok', text: form.enabled ? 'Configuração salva. A partir de agora, as mudanças de etapa mapeadas são enviadas à Meta.' : 'Configuração salva. O envio está desligado.' })
    } catch (err) {
      setNotice({ tone: 'error', text: err.message || 'Não foi possível salvar.' })
    } finally {
      setBusy(null)
    }
  }

  async function sendTest() {
    setBusy('test')
    setNotice(null)
    try {
      const res = await api.test({ clientId, code: testCode.trim() })
      setNotice({ tone: 'ok', text: `Evento de teste enviado (${res.events_received ?? 1} recebido). Confira em Gerenciador de Eventos > Testar eventos.` })
    } catch (err) {
      setNotice({ tone: 'error', text: err.message || 'O teste falhou.' })
    } finally {
      setBusy(null)
    }
  }

  async function retry(row) {
    setBusy(row.id)
    setNotice(null)
    try {
      const res = await api.retry({ clientId, id: row.id })
      setNotice(res.status === 'sent'
        ? { tone: 'ok', text: 'Evento reenviado e aceito pela Meta.' }
        : { tone: 'warn', text: res.error || 'O evento continua sem ser enviado.' })
      await loadLog()
    } catch (err) {
      setNotice({ tone: 'error', text: err.message || 'Não foi possível reenviar.' })
    } finally {
      setBusy(null)
    }
  }

  const columns = [
    { key: 'created_at', label: 'Quando', render: (v) => formatWhen(v) },
    { key: 'deal_titulo', label: 'Negócio', render: (v) => v || '—' },
    { key: 'event_name', label: 'Evento', render: (v) => data?.events?.find((e) => e.value === v)?.label || v },
    { key: 'source', label: 'Origem', render: (v) => SOURCE_LABELS[v] || v },
    { key: 'summary', label: 'Valor', render: (v) => formatValue(v) },
    { key: 'identifiers', label: 'Identificadores', sortable: false, render: (_, row) => identifiersText(row.summary) },
    {
      key: 'status', label: 'Resultado',
      render: (v, row) => (
        <span title={row.error || undefined}>
          <span className={`badge badge-${LOG_STATUS[v]?.tone || 'neutral'}`}>{LOG_STATUS[v]?.label || v}</span>
          {row.error && <div className="field-hint mcv-log-error">{row.error}</div>}
        </span>
      ),
    },
    {
      key: 'actions', label: '', sortable: false,
      render: (_, row) => (canRetry(row)
        ? <button type="button" className="btn btn-outline btn-xs" disabled={busy === row.id} onClick={() => retry(row)}>{busy === row.id ? 'Enviando…' : 'Reenviar'}</button>
        : null),
    },
  ]

  let body
  if (!isOrgAdmin) {
    body = <div className="mcn-banner is-warn">Apenas owner e admin da organização podem configurar a Conversions API.</div>
  } else if (!clientId) {
    body = <div className="mcn-banner">Selecione um cliente no topo da tela para configurar as conversões.</div>
  } else if (loadError || !data || !form || data.accounts.length === 0) {
    body = (
      <PageState
        loading={!loadError && (!data || !form)}
        error={loadError}
        notConnected={Boolean(data) && data.accounts.length === 0}
        loadingText="Carregando…"
        notConnectedText="Este cliente não tem conta de anúncios da Meta vinculada."
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    )
  } else {
    const visibleErrors = showErrors ? errors : {}
    body = (
      <>
        {demo && (
          <div className="mcn-banner is-warn">
            Modo demonstração (somente desenvolvimento): nada é enviado à Meta e os dados são de exemplo.
          </div>
        )}
        <div className="mcn-banner">
          Envia à Meta o que acontece depois do clique no anúncio (lead qualificado, negócio ganho, valor), para as campanhas
          otimizarem para venda real. E-mail e telefone saem com hash (SHA-256). Só as mudanças de etapa que acontecerem
          depois de ligar o envio são enviadas.
        </div>
        {notice && <div className={`mcn-banner is-${notice.tone === 'ok' ? 'ok' : notice.tone === 'warn' ? 'warn' : 'error'}`} role="status">{notice.text}</div>}

        <Card
          title="Conexão"
          sub="Conta de anúncios e dataset que recebem os eventos."
          aside={<span className={`badge ${form.enabled && saved?.enabled ? 'badge-green' : 'badge-neutral'}`}>{form.enabled && saved?.enabled ? 'Envio ativo' : 'Envio desligado'}</span>}
        >
          <div className="mcn-grid-2">
            <Field label="Conta de anúncios" error={visibleErrors.accountId}>
              <select className="input" value={form.accountId} onChange={(e) => set({ accountId: e.target.value, datasetId: '' })}>
                {data.accounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
              </select>
            </Field>
            <Field label="Dataset (pixel)" error={visibleErrors.datasetId} hint={pixels.warning || (pixels.list.length ? null : 'Nenhum dataset encontrado. Informe o ID numérico.')}>
              {pixels.list.length > 0 ? (
                <select className="input" value={form.datasetId} onChange={(e) => set({ datasetId: e.target.value })}>
                  <option value="">Selecione…</option>
                  {pixels.list.map((px) => <option key={px.id} value={px.id}>{px.name} ({px.id})</option>)}
                </select>
              ) : (
                <input className="input" inputMode="numeric" value={form.datasetId} onChange={(e) => set({ datasetId: e.target.value.replace(/\D/g, '') })} placeholder="ID do dataset" />
              )}
            </Field>
          </div>
          <label className="toggle">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
            <span className="toggle-slider" />
            <span className="toggle-label">Enviar eventos à Meta</span>
          </label>
          {visibleErrors.map && <div className="mcn-error" role="alert">{visibleErrors.map}</div>}
        </Card>

        <Card title="Eventos por etapa" sub={`Quando um negócio chega à etapa, o evento é enviado. ${mappedCount(form.map)} etapa(s) mapeada(s).`}>
          {groups.length === 0 && <div className="field-hint">Este cliente não tem funis com etapas.</div>}
          {groups.map((group) => (
            <div key={group.id} className="mcv-group">
              <div className="field-label">{group.name}</div>
              {group.stages.map((stage) => (
                <div className="mcv-row" key={stage.id}>
                  <div>
                    <strong>{stage.nome}</strong>
                    {stage.is_ganho && <span className="badge badge-green mcv-badge">Ganho</span>}
                    {stage.is_perdido && <span className="badge badge-red mcv-badge">Perdido</span>}
                  </div>
                  <select
                    className="input mcv-select" aria-label={`Evento da etapa ${stage.nome}`}
                    value={form.map[stage.id] || ''} onChange={(e) => setEvent(stage.id, e.target.value)}
                  >
                    <option value="">Não enviar</option>
                    {data.events.map((ev) => <option key={ev.value} value={ev.value}>{ev.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          ))}
          <div className="field-hint">O valor do evento é o valor do negócio. Também é possível enviar eventos por Automações, com o nó &quot;Enviar evento à Meta&quot;.</div>
        </Card>

        <div className="mcn-footer mcv-footer">
          <span className="field-hint">{dirty ? 'Há alterações não salvas.' : 'Tudo salvo.'}</span>
          <button type="button" className="btn btn-black" disabled={busy === 'save' || !dirty} onClick={save}>
            {busy === 'save' ? 'Salvando…' : 'Salvar configuração'}
          </button>
        </div>

        <Card title="Evento de teste" sub="Gerenciador de Eventos > Testar eventos mostra um código. Cole aqui para ver o evento chegar sem afetar os dados reais.">
          {!saved && <div className="field-hint mcv-hint-gap">Salve a configuração antes de testar.</div>}
          <div className="page-toolbar mcv-test-row">
            <input className="input mcv-select" value={testCode} onChange={(e) => setTestCode(e.target.value)} placeholder="Código, ex.: TEST12345" aria-label="Código do evento de teste" />
            <button type="button" className="btn btn-outline" disabled={!saved || busy === 'test' || !/^[A-Za-z0-9_-]{3,40}$/.test(testCode.trim())} onClick={sendTest}>
              {busy === 'test' ? 'Enviando…' : 'Enviar evento de teste'}
            </button>
          </div>
        </Card>

        <Card title="Log de envios" sub="Cada tentativa de envio, com o resultado. Falhas e eventos pulados podem ser reenviados.">
          <div className="mcn-checks mcv-checks">
            {LOG_FILTERS.map((f) => (
              <button type="button" key={f.value || 'all'} className={`mcn-check${filter === f.value ? ' is-on' : ''}`} onClick={() => setFilter(f.value)}>{f.label}</button>
            ))}
          </div>
          <DataTable columns={columns} data={log} loading={logLoading} emptyText="Nenhum envio registrado ainda." pagination pageSize={10} />
        </Card>
      </>
    )
  }

  return (
    <Layout title="Meta Ads — Conversões" breadcrumb={BREADCRUMB}>
      <div className="mcn">{body}</div>
    </Layout>
  )
}
